// components/AssistantOrb.tsx — "Ask Lotus" as a floating orb. It starts bottom-right; drag it anywhere and it stays
// there (per browser). A tap opens the assistant, a drag only moves it.
import { useEffect, useRef, useState } from 'react';
import { Sparkles } from 'lucide-react';

const KEY = 'lotus.orb';
const SIZE = 52;
const MARGIN = 16;
type Pos = { x: number; y: number };

const clamp = (p: Pos): Pos => ({
  x: Math.min(Math.max(p.x, MARGIN), window.innerWidth - SIZE - MARGIN),
  y: Math.min(Math.max(p.y, MARGIN), window.innerHeight - SIZE - MARGIN),
});
const home = (lift: number): Pos => ({ x: window.innerWidth - SIZE - 24, y: window.innerHeight - SIZE - 24 - lift });

export function AssistantOrb({ onOpen, lift = 0 }: { onOpen: () => void; /** Extra room above a bottom tab bar. */ lift?: number }) {
  const [pos, setPos] = useState<Pos>(() => {
    try { const raw = localStorage.getItem(KEY); if (raw) return clamp(JSON.parse(raw) as Pos); } catch { /* private mode */ }
    return home(lift);
  });
  const drag = useRef<{ dx: number; dy: number; moved: boolean } | null>(null);

  // Keep it on screen when the window changes size.
  useEffect(() => {
    const onResize = () => setPos((p) => clamp(p));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y, moved: false };
  };
  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const d = drag.current; if (!d) return;
    const next = clamp({ x: e.clientX - d.dx, y: e.clientY - d.dy });
    if (!d.moved && Math.hypot(next.x - pos.x, next.y - pos.y) < 4) return;
    d.moved = true;
    setPos(next);
  };
  const onPointerUp = () => {
    const d = drag.current; drag.current = null;
    if (!d) return;
    if (d.moved) { try { localStorage.setItem(KEY, JSON.stringify(pos)); } catch { /* private mode */ } }
    else onOpen();
  };

  return (
    <button type="button" onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}
      title="Ask Lotus (⌘K) · drag to move" aria-label="Ask Lotus"
      style={{ left: pos.x, top: pos.y, width: SIZE, height: SIZE, touchAction: 'none' }}
      className={`fixed z-40 grid cursor-grab select-none place-items-center rounded-full bg-bronze-500 text-on-primary shadow-float transition-colors hover:bg-bronze-600 active:cursor-grabbing`}>
      <Sparkles className="h-5 w-5" />
    </button>
  );
}
